import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  parseStringParamFromRouterProps,
  RCPWithQueryParams,
} from 'util/routing';
import { Filter } from 'util/backendapi/models/api.interfaces';
import { useReportState } from 'hooks/use-report-state';
import { EMPTY_FUNC, reorderList } from 'util/misc';
import {
  RouteMarchObsPointListColumns,
  RouteMarchObsPointListFilters,
  RouteMarchObsPointListView,
} from './RouteMarchObsPointListView';
import { routeMarchObsPointListDuck } from 'ducks/route-march/observation-points/list';
import { deleteApi, patchApi, postApi } from 'util/backendapi/fetch';
import { useGetApi } from 'hooks/use-get-api';
import { errorToString } from 'util/backendapi/error';
import { RouteMarch } from 'util/backendapi/types/Model';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';

type OwnProps = RCPWithQueryParams<Filter.ReportsRouteMarchObservationPoint>;

export const RouteMarchObsPointListScreen = (ownProps: OwnProps) => {
  const routeMarchCode = parseStringParamFromRouterProps(
    ownProps,
    'routeMarchCode'
  );

  const [routeMarchDetail, setUrl, setParams] = useGetApi(
    '' as '/route-marches/'
  );

  const [reorderingErrorMessage, setReorderingErrorMessage] =
    useState<string>('');

  useEffect(() => {
    // Handle routeMarchCode in the url changing (e.g. after copy route march)
    setUrl('/route-marches/');
    setParams({
      code: routeMarchCode,
    });
  }, [routeMarchCode, setUrl, setParams]);

  const routeMarch =
    routeMarchDetail.data && routeMarchDetail.data.length > 0
      ? routeMarchDetail.data[0]
      : null;

  const columns = useMemo(() => {
    return RouteMarchObsPointListColumns({
      refreshList: EMPTY_FUNC,
      removeObsPoint: EMPTY_FUNC,
    });
  }, []);

  const removeObsPoint = useCallback(
    async (routeMarchObservationPointId: number) => {
      await deleteApi(
        `/route-march-observation-points/${routeMarchObservationPointId}/`,
        {}
      );
    },
    []
  );

  const copyRouteMarch = useCallback(
    async (
      routeMarchId: number,
      values: Pick<RouteMarch, 'code' | 'description'>
    ) => {
      return await postApi(`/route-marches/${routeMarchId}/copy/`, values);
    },
    []
  );

  const createRouteMarchObsPoint = useCallback(
    async (
      routeMarchId: number,
      observationPointId: number,
      position: number
    ) => {
      await postApi(`/route-march-observation-points/`, {
        route_march: routeMarchId,
        observation_point: observationPointId,
        position: position,
      });
    },
    []
  );

  const props = {
    reportProps: useReportState(
      ownProps,
      columns,
      RouteMarchObsPointListFilters,
      routeMarchObsPointListDuck,
      (state) => state.routeMarchObsPoint.list
    ),
  };

  const onRecordDragged = useCallback(
    async (sourceIndex: number, destIndex: number) => {
      const routeMarchObservationPoint =
        props.reportProps.records![sourceIndex];

      const existingPosition = routeMarchObservationPoint.position;
      const newPosition = props.reportProps.records![destIndex].position;

      const updatedRouteMarchObservationPoint = {
        ...routeMarchObservationPoint,
        position: newPosition,
      };

      const reorderedRows = reorderList(
        props.reportProps.records!,
        existingPosition,
        updatedRouteMarchObservationPoint
      );

      // Update order with loading state so report table is disabled
      props.reportProps.updateReportRows!(reorderedRows, true);

      try {
        await patchApi(
          `/route-march-observation-points/${routeMarchObservationPoint.id}/`,
          {
            position: newPosition,
          }
        );
      } catch (e) {
        setReorderingErrorMessage(i18n._(t`Error updating positions`));
        return;
      }

      // Remove loading status
      props.reportProps.updateReportRows!(reorderedRows, false);
    },
    [props.reportProps, setReorderingErrorMessage]
  );

  const errorMessage = reorderingErrorMessage
    ? reorderingErrorMessage
    : routeMarchDetail.isError
    ? errorToString(routeMarchDetail.error)
    : '';

  return (
    <RouteMarchObsPointListView
      {...props}
      routeMarch={routeMarch}
      removeObsPoint={removeObsPoint}
      onRecordDragged={onRecordDragged}
      copyRouteMarch={copyRouteMarch}
      createRouteMarchObsPoint={createRouteMarchObsPoint}
      isLoading={routeMarchDetail.isLoading}
      errorMessage={errorMessage}
    />
  );
};
